import { useRef, useState } from 'react';

interface Props {
  titulo: string;
  subtitulo: string;
  insight: string;
  icon: string;
  cor: string;
}

export default function CardCompartilhavel({ titulo, subtitulo, insight, icon, cor }: Props) {
  const [legendaCopiada, setLegendaCopiada] = useState(false);
  const [instrucaoAberta, setInstrucaoAberta] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const padraoSlug = titulo.replace('O Padrão ', '').toLowerCase();

  const legenda = `🧠 Fiz o Diagnóstico de Padrão Mental da @bia.holmes e descobri que meu padrão é: "${titulo.replace('O Padrão ', '')}"

${insight}

Você sabe qual é o seu padrão? Faça grátis — link na bio ✨

#ReprogramacaoMental #PNL #BiaHolmes #MindsetDeElite #AltaPerformance #Autoconhecimento`;

  const copiarLegenda = () => {
    navigator.clipboard.writeText(legenda).then(() => {
      setLegendaCopiada(true);
      setTimeout(() => setLegendaCopiada(false), 2500);
    });
  };

  const gradientMap: Record<string, string> = {
    'from-rose-500 to-rose-700': 'from-rose-900/80 to-[#0d0d0d]',
    'from-amber-500 to-amber-700': 'from-amber-900/80 to-[#0d0d0d]',
    'from-orange-500 to-orange-700': 'from-orange-900/80 to-[#0d0d0d]',
    'from-emerald-600 to-emerald-800': 'from-emerald-900/80 to-[#0d0d0d]',
    'from-indigo-600 to-indigo-800': 'from-indigo-900/80 to-[#0d0d0d]',
  };

  const bgGrad = gradientMap[cor] || 'from-gold-900/60 to-[#0d0d0d]';

  return (
    <div className="mt-10">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-px flex-1 bg-gold-500/15" />
        <span className="text-gold-400 text-xs font-semibold tracking-[0.3em] uppercase font-['Montserrat']">
          Compartilhe no Instagram
        </span>
        <div className="h-px flex-1 bg-gold-500/15" />
      </div>

      <div className="grid md:grid-cols-2 gap-6 items-start">

        {/* Card Stories — visual */}
        <div className="flex flex-col items-center">
          <p className="text-white/30 text-xs font-['Montserrat'] mb-3 text-center">
            Formato Stories (9:16) — tire um print desta área
          </p>

          {/* Card no formato story */}
          <div
            ref={cardRef}
            className={`relative w-64 rounded-3xl overflow-hidden border border-gold-500/20`}
            style={{ aspectRatio: '9/16' }}
          >
            {/* Background com imagem */}
            <img
              src="https://public.readdy.ai/ai/img_res/5422198b6d3c833b5215c5f59cb6b0f6.jpg"
              alt="background"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className={`absolute inset-0 bg-gradient-to-b ${bgGrad} opacity-90`} />

            {/* Conteúdo do card */}
            <div className="relative z-10 flex flex-col h-full p-6 justify-between">

              {/* Topo — branding */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-5 flex items-center justify-center bg-gold-500/20 rounded-md">
                    <i className="ri-quill-pen-fill text-gold-400 text-[10px]" />
                  </div>
                  <span className="text-white/60 text-[10px] font-['Montserrat'] font-semibold">@bia.holmes</span>
                </div>
                <div className="px-2 py-0.5 bg-gold-500/20 border border-gold-500/30 rounded-full">
                  <span className="text-gold-400 text-[9px] font-bold font-['Montserrat'] uppercase tracking-widest">Diagnóstico</span>
                </div>
              </div>

              {/* Centro — resultado */}
              <div className="text-center">
                <p className="text-white/45 text-[11px] font-['Montserrat'] uppercase tracking-widest mb-3">
                  Meu padrão mental é
                </p>
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-2xl bg-gold-500/15 border border-gold-500/25">
                  <i className={`${icon} text-gold-400 text-3xl`} />
                </div>
                <h2 className="font-display text-2xl font-bold text-white leading-tight mb-1">
                  {titulo.replace('O Padrão ', '')}
                </h2>
                <p className="text-gold-400 text-xs font-['Montserrat'] font-semibold mb-4">
                  {subtitulo}
                </p>
                <p className="text-white/50 text-[11px] font-['Montserrat'] leading-relaxed italic px-2">
                  &ldquo;{insight.slice(0, 90)}...&rdquo;
                </p>
              </div>

              {/* Rodapé — CTA */}
              <div className="text-center">
                <div className="bg-gold-500/15 border border-gold-500/25 rounded-xl px-4 py-3 mb-3">
                  <p className="text-white/60 text-[10px] font-['Montserrat']">Descubra o seu padrão</p>
                  <p className="text-gold-400 text-xs font-bold font-['Montserrat']">🔗 Link na bio</p>
                </div>
                <div className="flex items-center justify-center gap-1">
                  {['#PNL', '#Mindset', '#BiaHolmes'].map((h, i) => (
                    <span key={i} className="text-white/25 text-[9px] font-['Montserrat']">{h}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Como salvar */}
          <button
            onClick={() => setInstrucaoAberta(!instrucaoAberta)}
            className="mt-4 flex items-center gap-1.5 text-white/35 hover:text-white/60 text-xs font-['Montserrat'] transition-colors cursor-pointer"
          >
            <i className={`${instrucaoAberta ? 'ri-arrow-up-s-line' : 'ri-information-line'} text-sm`} />
            Como salvar para o Instagram
          </button>

          {instrucaoAberta && (
            <div className="mt-3 w-full max-w-xs bg-[#111] border border-gold-500/15 rounded-xl p-4">
              <div className="space-y-2.5">
                {[
                  { n: '1', txt: 'Tire um print desta tela' },
                  { n: '2', txt: 'Recorte apenas o card acima' },
                  { n: '3', txt: 'Poste nos Stories como imagem' },
                  { n: '4', txt: 'Cole a legenda pronta abaixo' },
                ].map(step => (
                  <div key={step.n} className="flex items-center gap-2.5">
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-gold-500/20 border border-gold-500/30 flex-shrink-0">
                      <span className="text-gold-400 text-[10px] font-bold font-['Montserrat']">{step.n}</span>
                    </div>
                    <span className="text-white/50 text-xs font-['Montserrat']">{step.txt}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Legenda pronta */}
        <div className="flex flex-col gap-4">
          <div className="bg-[#111111] border border-white/6 rounded-2xl overflow-hidden">
            {/* Header legenda */}
            <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-white/5">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-instagram-line text-pink-400 text-base" />
              </div>
              <span className="text-white/60 text-sm font-semibold font-['Montserrat']">Legenda pronta para copiar</span>
            </div>

            {/* Texto da legenda */}
            <div className="p-5">
              <pre className="text-white/50 text-xs font-['Montserrat'] leading-relaxed whitespace-pre-wrap break-words select-all">
                {legenda}
              </pre>
            </div>

            {/* Botão copiar */}
            <div className="px-5 pb-5">
              <button
                onClick={copiarLegenda}
                className={`flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-xs font-['Montserrat'] transition-all cursor-pointer whitespace-nowrap border ${
                  legendaCopiada
                    ? 'bg-green-500/15 border-green-500/30 text-green-400'
                    : 'bg-gold-500/10 border-gold-500/25 text-gold-400 hover:bg-gold-500/20'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${legendaCopiada ? 'ri-check-double-line' : 'ri-clipboard-line'} text-sm`} />
                </div>
                {legendaCopiada ? 'Copiado com sucesso!' : 'Copiar legenda completa'}
              </button>
            </div>
          </div>

          {/* Dicas de engajamento */}
          <div className="bg-[#111111] border border-white/6 rounded-2xl p-5">
            <p className="text-white/30 text-[10px] font-semibold tracking-widest uppercase font-['Montserrat'] mb-4">
              Dicas para mais engajamento
            </p>
            <div className="space-y-3">
              {[
                { icon: 'ri-time-line', txt: 'Poste entre 18h–21h para maior alcance' },
                { icon: 'ri-question-answer-line', txt: 'Crie uma enquete: "Qual é o seu padrão?" nos stories' },
                { icon: 'ri-link-m', txt: 'Use o link /testes na bio para direcionar' },
                { icon: 'ri-chat-3-line', txt: 'Convide: "Me conta o seu nos comentários"' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`${item.icon} text-gold-400/60 text-xs`} />
                  </div>
                  <span className="text-white/40 text-xs font-['Montserrat'] leading-relaxed">{item.txt}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA planos */}
          <a
            href="/planos"
            className="flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-r from-gold-500 to-gold-700 text-[#0a0a0a] font-bold text-sm font-['Montserrat'] hover:from-gold-400 hover:to-gold-600 transition-all whitespace-nowrap"
          >
            <i className="ri-vip-crown-line" />
            Ir além: Acessar protocolo completo
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      </div>
    </div>
  );
}
